import {
  examplesId,
  labelValue,
  getInputProps,
  ariaDescribedByIds,
} from "@rjsf/utils";
import {
  addCommas,
  removeCommas,
  numberToWords,
  digitsFaToEn,
} from "@persian-tools/persian-tools";
import { InputAdornment, Typography, TextField } from "@mui/material";

const TYPES_THAT_SHRINK_LABEL = ["date", "datetime-local", "file", "time"];

/** The `BaseInputTemplate` is the template to use to render the basic `<input>` component for the `core` theme.
 * It is used as the template for rendering many of the <input> based widgets that differ by `type` and callbacks only.
 * It can be customized/overridden for other themes or individual implementations as needed.
 *
 * @param props - The `WidgetProps` for this template
 */
export default function BaseInputTemplate(props) {
  const {
    id,
    name, // remove this from textFieldProps
    type,
    label,
    value,
    schema,
    onBlur,
    onFocus,
    options,
    onChange,
    required,
    readonly,
    uiSchema,
    disabled,
    registry,
    hideLabel,
    hideError,
    autofocus,
    placeholder,
    rawErrors = [],
    onChangeOverride,
    errorSchema,
    formContext,
    InputLabelProps,
    ...textFieldProps
  } = props;
  const inputProps = getInputProps(schema, type, options);
  const { adornment, convertToWord = false } = options;

  // Now we need to pull out the step, min, max into an inner `inputProps` for material-ui
  const { step, min, max, ...rest } = inputProps;
  const otherProps = {
    inputProps: {
      step,
      min,
      max,
      ...(schema.examples ? { list: examplesId(id) } : undefined),
    },
    // ...rest,
  };

  const isNumber = inputProps?.type === "number";
  const direction = isNumber ? "ltr" : "rtl";

  const _onChange = ({ target: { value } }) =>
    onChange(
      value === "" ? options.emptyValue : isNumber ? removeCommas(value) : digitsFaToEn(value)
    );
  const _onBlur = ({ target: { value } }) => onBlur(id, value);
  const _onFocus = ({ target: { value } }) => onFocus(id, value);
  const DisplayInputLabelProps = TYPES_THAT_SHRINK_LABEL.includes(type)
    ? {
        ...InputLabelProps,
        shrink: true,
      }
    : InputLabelProps;

  return (
    <>
      <TextField
        {...otherProps}
        {...textFieldProps}
        id={id}
        name={id}
        dir={direction}
        onBlur={_onBlur}
        onFocus={_onFocus}
        required={required}
        autoFocus={autofocus}
        placeholder={placeholder}
        error={rawErrors.length > 0}
        disabled={disabled || readonly}
        onChange={onChangeOverride || _onChange}
        InputLabelProps={DisplayInputLabelProps}
        label={labelValue(label || undefined, hideLabel, undefined)}
        aria-describedby={ariaDescribedByIds(id, !!schema.examples)}
        value={
          value && isNumber && convertToWord && !isNaN(Number(value))
            // ? addCommas(Number(value))
            ? (Number(value))
            : value || ""
        }
        helperText={
          isNumber &&
          convertToWord &&
          !isNaN(Number(value)) &&
          Number(value) > 0 &&
          Number(value) < 999999999999999 && (
            <Typography variant="caption" color="text.disabled" fontSize={11}>
              {numberToWords(String(Number(value).toFixed(0)))} {adornment}
            </Typography>
          )
        }
        slotProps={{
          input: {
            startAdornment: adornment && (
              <InputAdornment position="start" sx={{ px: 0.5, minWidth: 40 }}>
                <Typography variant="caption" fontSize={11}>
                  {adornment}
                </Typography>
              </InputAdornment>
            ),
          },
        }}
      />
      {Array.isArray(schema.examples) && (
        <datalist id={examplesId(id)}>
          {schema.examples
            .concat(
              schema.default && !schema.examples.includes(schema.default)
                ? [schema.default]
                : []
            )
            .map((example) => {
              return <option key={example} value={example} />;
            })}
        </datalist>
      )}
    </>
  );
}
